import type * as React from "react"
import { Button as BaseButton } from "@/components/ui/button"
import { Input as BaseInput } from "@/components/ui/input"
import {
	Select as BaseSelect,
	SelectTrigger as BaseSelectTrigger,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

// Form Input with unified height
function Input({ className, ...props }: React.ComponentProps<"input">) {
	return (
		<BaseInput className={cn("h-10 md:h-12 ring-0!", className)} {...props} />
	)
}

// Form Button with unified height
function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> & React.ComponentProps<typeof BaseButton>) {
	return (
		<BaseButton
			className={cn("h-10 md:h-12 mt-3 md:mt-5 ", className)}
			variant={variant}
			size={size}
			asChild={asChild}
			{...props}
		/>
	)
}

// Form Select Trigger with unified height
function SelectTrigger({
	className,
	children,
	...props
}: React.ComponentProps<typeof BaseSelectTrigger>) {
	return (
		<BaseSelectTrigger
			className={cn(
				"h-10 md:h-12 data-[size=default]:h-10 data-[size=sm]:h-10 md:data-[size=default]:h-12 md:data-[size=sm]:h-12 ring-0!",
				className,
			)}
			{...props}
		>
			{children}
		</BaseSelectTrigger>
	)
}

export const FormContainer = ({
	children,
	className,
	...props
}: { children: React.ReactNode } & React.ComponentProps<"div">) => {
	return (
		<div
			className={cn(
				"w-71.25 md:w-[444px]  p-0 pt-12 pb-12  md:pb-[200px] mx-auto   flex flex-col items-stretch",
				className,
			)}
			{...props}
		>
			{children}
		</div>
	)
}

export const FormTitle = ({
	children,
	className,
	...props
}: { children: React.ReactNode } & React.ComponentProps<"h1">) => {
	return (
		<h2
			className={cn(
				"text-base md:text-3xl leading-none   font-medium  text-center",
				className,
			)}
			{...props}
		>
			{children}
		</h2>
	)
}

export function FormDescription({
	children,
	className,
	...props
}: { children: React.ReactNode } & React.ComponentProps<"p">) {
	return (
		<p
			className={cn(
				"text-center text-xs md:text-base text-hint px-2 md:px-0 mt-2 md:mt-3",
				className,
			)}
			{...props}
		>
			{children}
		</p>
	)
}

export const FormHeader = ({
	children,
	className,
	...props
}: { children: React.ReactNode } & React.ComponentProps<"div">) => {
	return (
		<div className={cn("mt-12 md:mt-30 mb-6 md:mb-8", className)} {...props}>
			{children}
		</div>
	)
}

export const FormMain = ({
	children,
	className,
	...props
}: { children: React.ReactNode } & React.ComponentProps<"div">) => {
	return (
		<div
			className={cn(
				"flex flex-col gap-3 w-71.25 md:w-[444px] mx-auto",
				className,
			)}
			{...props}
		>
			{children}
		</div>
	)
}

// Re-export Select and other Select components
const Select = BaseSelect

export {
	Input,
	Button,
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
}
